/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useRef, useState} from "react";
import {Link, useLocation, useParams} from "react-router-dom";
import {AlertTriangle, CreditCard, Loader2} from "lucide-react";
import {SiteHeader} from "@/components/SiteHeader";
import {WhatsAppButton} from "@/components/WhatsAppButton";
import {BookingSuccess} from "@/components/booking/BookingSuccess";
import {api, fmtDate, money, WA_LINK} from "@/lib/inner";
import {paySnap} from "@/lib/snap";

const Waiting = ({b, onPay, onRetry, busy}) => {
  const failed = b.status === "payment_failed";
  return <main className="success-page" data-testid="payment-pending">
    <div className="success-mark pending">{failed ? <AlertTriangle/> : <CreditCard/>}</div>
    <p className="eyebrow">{failed ? "PEMBAYARAN BELUM BERHASIL" : "MENUNGGU PEMBAYARAN DP"}</p>
    <h1>Satu langkah lagi,<br/><em>bayar DP-mu.</em></h1>
    <p className="lead">Booking <strong>{b.code}</strong> di Outlet {b.branch_name}, {fmtDate(b.date)} pukul {b.start_time} WIB. Jam belum ditahan, jadi selesaikan pembayaran agar booking terkonfirmasi.</p>
    <div className="receipt"><div><span>DP</span><strong data-testid="pending-dp">{money(b.payment?.dp_amount)}</strong></div><div><span>Total perawatan</span><strong>{money(b.price)}</strong></div></div>
    {failed ? <button className="primary-btn" data-testid="payment-retry-button" disabled={busy} onClick={onRetry}>Coba bayar lagi</button>
      : <button className="primary-btn" data-testid="payment-pay-button" disabled={busy} onClick={onPay}>Bayar DP sekarang</button>}
    <p className="muted checking"><Loader2 size={14} className="spin"/> Status pembayaran diperbarui otomatis.</p>
    <Link className="subtle-link" to="/booking">Buat booking baru</Link>
  </main>;
};

const Conflict = ({b}) => <main className="success-page" data-testid="payment-conflict">
  <div className="success-mark pending"><AlertTriangle/></div>
  <p className="eyebrow">DP DITERIMA · JAM PERLU DIATUR ULANG</p>
  <h1>Maaf, jam ini<br/><em>sudah terisi.</em></h1>
  <p className="lead">DP {money(b.dp_paid)} untuk booking <strong>{b.code}</strong> sudah kami terima, tetapi jam {b.start_time} WIB terisi lebih dulu. Tim kami akan menghubungimu untuk memilih jam baru atau pengembalian dana.</p>
  <a className="primary-btn" href={WA_LINK} target="_blank" rel="noreferrer">Hubungi Customer Care</a>
</main>;

const Cancelled = ({b}) => <main className="success-page" data-testid="booking-cancelled">
  <div className="success-mark pending"><AlertTriangle/></div>
  <p className="eyebrow">BOOKING {b.status === "no_show" ? "TIDAK HADIR" : "DIBATALKAN"}</p>
  <h1>Booking ini<br/><em>tidak aktif.</em></h1>
  <p className="lead">Booking <strong>{b.code}</strong> sudah {b.status === "no_show" ? "ditandai tidak hadir" : "dibatalkan"}. Hubungi kami jika ingin menjadwalkan ulang.</p>
  <a className="primary-btn" href={WA_LINK} target="_blank" rel="noreferrer">Hubungi Customer Care</a>
</main>;

export default function BookingStatus() {
  const {code} = useParams();
  const auto = useLocation().state?.autoPay;
  const [b, setB] = useState(null), [cfg, setCfg] = useState(null), [busy, setBusy] = useState(false), [missing, setMissing] = useState(false);
  const opened = useRef(false);
  const refresh = () => api.get(`/bookings/${code}/status`).then(r => setB(r.data)).catch(e => e.response?.status === 404 && setMissing(true));
  const pay = (token = b?.payment?.snap_token) => token && paySnap(cfg, token, refresh).catch(() => alert("Halaman pembayaran gagal dimuat. Coba lagi."));
  const retry = async () => {
    setBusy(true);
    try { const r = await api.post(`/bookings/${code}/pay`); setB(r.data); pay(r.data.payment.snap_token); } catch (e) { alert(e.response?.data?.detail || "Belum bisa membuat pembayaran."); }
    setBusy(false);
  };
  useEffect(() => { api.get("/public/config").then(r => setCfg(r.data.payment)); refresh(); }, [code]);
  useEffect(() => {
    if (!b || !["pending_payment", "payment_failed"].includes(b.status)) return;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [b?.status]);
  useEffect(() => { if (auto && cfg && b?.status === "pending_payment" && !opened.current) { opened.current = true; pay(); } }, [cfg, b]);

  let body = <main className="success-page"><p className="muted">Memuat booking…</p></main>;
  if (missing) body = <main className="success-page"><h1>Booking <em>tidak ditemukan.</em></h1><Link className="primary-btn" to="/booking">Buat booking</Link></main>;
  else if (b && b.status === "slot_conflict") body = <Conflict b={b}/>;
  else if (b && ["cancelled", "no_show"].includes(b.status)) body = <Cancelled b={b}/>;
  else if (b && ["pending_payment", "payment_failed"].includes(b.status)) body = <Waiting b={b} busy={busy || !cfg} onPay={() => pay()} onRetry={retry}/>;
  else if (b) body = <BookingSuccess done={b}/>;
  return <><SiteHeader/>{body}<WhatsAppButton/></>;
}
